import { useEffect, useMemo, useState } from "react";
import {
  Download,
  Loader2,
  Trash2,
  UploadCloud,
  WalletCards,
  FileText,
  Sparkles,
} from "lucide-react";

import {
  deletePayroll,
  downloadPayroll,
  storePayroll,
} from "../api/admin-panel";

import type { AdminPanelUser, PayrollRecord } from "../types/admin-panel";

type Props = {
  users: AdminPanelUser[];
  payrolls: PayrollRecord[];
  loading?: boolean;
  onReload?: () => Promise<void> | void;
};

export function PayrollPanel({
  users,
  payrolls,
  loading = false,
  onReload,
}: Props) {
  const [usuarioId, setUsuarioId] = useState("");
  const [mes, setMes] = useState(String(new Date().getMonth() + 1));
  const [anio, setAnio] = useState(String(new Date().getFullYear()));
  const [tipo, setTipo] = useState("normal");
  const [archivo, setArchivo] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterType, setFilterType] = useState("");
  const [page, setPage] = useState(1);

  const ITEMS_PER_PAGE = 4;

  const empleados = users
    .slice()
    .sort((a, b) => a.nombre.localeCompare(b.nombre));

  const filteredPayrolls = useMemo(() => {
    return payrolls.filter((item) => {
      const matchesSearch =
        !search || item.empleado.toLowerCase().includes(search.toLowerCase());

      const matchesMonth = !filterMonth || String(item.mes) === filterMonth;

      const matchesYear = !filterYear || String(item.anio) === filterYear;

      const matchesType = !filterType || item.tipo === filterType;

      return matchesSearch && matchesMonth && matchesYear && matchesType;
    });
  }, [payrolls, search, filterMonth, filterYear, filterType]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPayrolls.length / ITEMS_PER_PAGE),
  );

  const paginatedPayrolls = filteredPayrolls.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    setPage(1);
  }, [search, filterMonth, filterYear, filterType]);

  const onSubmit = async () => {
    if (!usuarioId || !mes || !anio || !archivo) return;

    setSaving(true);

    try {
      await storePayroll({
        usuario_id: Number(usuarioId),
        mes: Number(mes),
        anio: Number(anio),
        tipo,
        archivo,
      });

      setArchivo(null);

      await onReload?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="scroll-reveal-right space-y-5">
      <section className="dashboard-scale-in group relative overflow-hidden rounded-[32px] border border-[#e7ecef] bg-white/90 shadow-[0_14px_45px_rgba(15,23,42,.055)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-emerald-100 hover:shadow-[0_24px_70px_rgba(15,23,42,.08)]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -right-24 top-[-90px] h-[260px] w-[260px] rounded-full bg-emerald-300/22 blur-3xl transition duration-500 group-hover:scale-110" />
          <div className="absolute bottom-[-120px] left-[-70px] h-[240px] w-[240px] rounded-full bg-teal-300/14 blur-3xl transition duration-500 group-hover:scale-110" />
        </div>

        <div className="relative flex flex-col gap-4 border-b border-[#edf1f3] bg-white/65 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em] text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.07)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-100">
              <Sparkles size={14} />
              Payroll Workspace
            </div>

            <div className="mt-4 flex items-start gap-4">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-[24px] border border-emerald-100 bg-emerald-50 text-emerald-700 shadow-[0_12px_28px_rgba(16,185,129,.10)] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105">
                <WalletCards size={24} />
              </div>

              <div>
                <h3 className="text-2xl font-black tracking-tight text-slate-950">
                  Gestión de nóminas
                </h3>

                <p className="mt-1 max-w-2xl text-sm font-semibold leading-6 text-slate-500">
                  Centraliza nóminas mensuales, pagas extra y documentación
                  salarial.
                </p>
              </div>
            </div>
          </div>

          <div className="grid min-w-[240px] gap-3 sm:grid-cols-2">
            <MiniStat label="Registros" value={String(payrolls.length)} />
            <MiniStat label="Empleados" value={String(users.length)} />
          </div>
        </div>

        <div className="relative grid gap-4 p-6 xl:grid-cols-5">
          <Field label="Empleado">
            <select
              value={usuarioId}
              onChange={(e) => setUsuarioId(e.target.value)}
              className="field-control"
              disabled={loading || saving}
            >
              <option value="">Selecciona empleado</option>

              {empleados.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Mes">
            <select
              value={mes}
              onChange={(e) => setMes(e.target.value)}
              className="field-control"
              disabled={saving}
            >
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Año">
            <input
              value={anio}
              onChange={(e) => setAnio(e.target.value)}
              className="field-control"
              type="number"
              disabled={saving}
            />
          </Field>

          <Field label="Tipo">
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="field-control"
              disabled={saving}
            >
              <option value="normal">Normal</option>
              <option value="extra_julio">Extra Julio</option>
              <option value="extra_diciembre">Extra Diciembre</option>
            </select>
          </Field>

          <Field label="Documento PDF">
            <label className="group/file flex h-[52px] cursor-pointer items-center justify-center gap-2 rounded-[20px] border border-dashed border-emerald-200 bg-emerald-50/70 px-4 text-sm font-black text-emerald-700 shadow-[0_8px_22px_rgba(16,185,129,.06)] transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-100/80 hover:shadow-[0_14px_32px_rgba(16,185,129,.12)]">
              <UploadCloud
                size={17}
                className="transition duration-300 group-hover/file:-translate-y-0.5 group-hover/file:scale-110"
              />
              Seleccionar archivo
              <input
                key={archivo ? archivo.name : "empty"}
                onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
                type="file"
                accept="application/pdf"
                disabled={saving}
                className="hidden"
              />
            </label>
          </Field>
        </div>

        {archivo && (
          <div className="relative px-6 pb-6">
            <div className="animate-slide-up flex items-center gap-3 rounded-[26px] border border-emerald-100 bg-emerald-50/80 p-4 text-sm font-bold text-slate-600 shadow-[0_10px_28px_rgba(16,185,129,.08)]">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-emerald-100 bg-white text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.08)]">
                <FileText size={18} />
              </div>

              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-black text-slate-950">
                  {archivo.name}
                </div>

                <div className="mt-1 text-xs font-semibold text-slate-500">
                  Archivo listo para subir
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="relative flex flex-col gap-3 border-t border-[#edf1f3] bg-slate-50/70 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-slate-500">
            Selecciona un empleado, periodo y documento PDF para registrar la
            nómina.
          </p>

          <button
            onClick={onSubmit}
            disabled={saving || !usuarioId || !archivo}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-5 py-3 text-sm font-black text-white shadow-[0_14px_32px_rgba(16,185,129,.20)] transition-all duration-300 hover:-translate-y-1 hover:scale-[1.01] hover:shadow-[0_20px_42px_rgba(16,185,129,.26)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:scale-100"
          >
            {saving ? (
              <Loader2 size={17} className="animate-spin" />
            ) : (
              <UploadCloud size={17} />
            )}
            Subir nómina
          </button>
        </div>
      </section>

      <section className="dashboard-scale-in overflow-hidden rounded-[32px] border border-[#e7ecef] bg-white/90 shadow-[0_14px_45px_rgba(15,23,42,.055)] backdrop-blur-xl transition-all duration-300 hover:border-emerald-100 hover:shadow-[0_22px_60px_rgba(15,23,42,.075)]">
        <div className="border-b border-[#edf1f3] px-5 py-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <h3 className="text-lg font-black text-slate-950">
                Historial de nóminas
              </h3>

              <p className="text-sm font-semibold text-slate-500">
                Consulta, descarga o elimina documentos salariales.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar empleado..."
                className="field-control min-w-[220px]"
              />

              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="field-control"
              >
                <option value="">Todos los meses</option>

                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>

              <input
                type="number"
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                placeholder="Año"
                className="field-control w-[120px]"
              />

              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="field-control"
              >
                <option value="">Todos los tipos</option>
                <option value="normal">Normal</option>
                <option value="extra_julio">Extra Julio</option>
                <option value="extra_diciembre">Extra Diciembre</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-5">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="mb-3 h-12 animate-pulse rounded-2xl bg-slate-100"
              />
            ))}
          </div>
        ) : filteredPayrolls.length === 0 ? (
          <div className="grid min-h-[220px] place-items-center p-8 text-center">
            <div>
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700 shadow-[0_10px_24px_rgba(16,185,129,.08)]">
                <WalletCards size={22} />
              </div>

              <p className="mt-4 text-sm font-black text-slate-950">
                Todavía no hay nóminas registradas.
              </p>

              <p className="mt-1 text-sm font-semibold text-slate-500">
                Cuando subas documentos salariales, aparecerán aquí.
              </p>
            </div>
          </div>
        ) : (
          <div className="max-h-[620px] overflow-auto">
            <table className="table-modern table-dark min-w-[980px]">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>Empleado</th>
                  <th>Periodo</th>
                  <th>Tipo</th>
                  <th>Documento</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>

              <tbody>
                {paginatedPayrolls.map((p) => (
                  <tr key={p.id} className="group">
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="grid h-10 w-10 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-sm font-black text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.06)] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105">
                          {getInitials(p.empleado)}
                        </div>

                        <span className="font-black text-slate-950">
                          {p.empleado}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="font-semibold text-slate-600">
                        {monthName(p.mes)} {p.anio}
                      </div>
                    </td>

                    <td>
                      <TypeBadge tipo={p.tipo} />
                    </td>

                    <td>
                      <div className="max-w-[260px] truncate font-semibold text-slate-500">
                        {p.archivo_nomina}
                      </div>
                    </td>

                    <td>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => downloadPayroll(p.id)}
                          className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-1 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 hover:shadow-[0_12px_28px_rgba(16,185,129,.10)]"
                          title="Ver / descargar"
                        >
                          <Download size={15} />
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            if (!confirm("¿Eliminar esta nómina?")) return;

                            await deletePayroll(p.id);

                            await onReload?.();
                          }}
                          className="grid h-9 w-9 place-items-center rounded-full border border-rose-100 bg-rose-50 text-rose-500 shadow-[0_8px_20px_rgba(244,63,94,.06)] transition-all duration-300 hover:-translate-y-1 hover:bg-rose-100 hover:text-rose-600 hover:shadow-[0_12px_28px_rgba(244,63,94,.12)]"
                          title="Eliminar"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-[#edf1f3] px-5 py-4">
          <div className="text-sm font-bold text-slate-500">
            Página {page} de {totalPages}
          </div>

          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-40"
            >
              Anterior
            </button>

            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[26px] border border-emerald-100 bg-white/80 px-5 py-4 shadow-[0_8px_22px_rgba(16,185,129,.06)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-50">
      <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
        {label}
      </div>

      <div className="mt-2 text-3xl font-black tracking-tight text-slate-950">
        {value}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-black uppercase tracking-[0.14em] text-slate-400">
        {label}
      </span>

      {children}
    </label>
  );
}

function TypeBadge({ tipo }: { tipo: string }) {
  return (
    <span className="inline-flex rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-700 transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-100">
      {formatTipo(tipo)}
    </span>
  );
}

function getInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

const months = [
  { value: "1", label: "Enero" },
  { value: "2", label: "Febrero" },
  { value: "3", label: "Marzo" },
  { value: "4", label: "Abril" },
  { value: "5", label: "Mayo" },
  { value: "6", label: "Junio" },
  { value: "7", label: "Julio" },
  { value: "8", label: "Agosto" },
  { value: "9", label: "Septiembre" },
  { value: "10", label: "Octubre" },
  { value: "11", label: "Noviembre" },
  { value: "12", label: "Diciembre" },
];

function monthName(mes: number) {
  return months.find((m) => Number(m.value) === mes)?.label ?? String(mes);
}

function formatTipo(tipo: string) {
  if (tipo === "extra_julio") return "Extra Julio";

  if (tipo === "extra_diciembre") return "Extra Diciembre";

  return "Normal";
}
